<script lang="ts" setup>
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useCreateIntervention} from '@/ts/store/intervention-store';
import {useLoadingData} from '@/ts/composables/pure/use-loading-data-ref';
import {
  watch,
  computed,
  onMounted,
} from 'vue';
import {useRouter} from 'vue-router';
import LoadingError from '@/vue/templates/loading-error.vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useUserStore} from '@/ts/store/user-store';
import {pagesPath} from '@/ts/router/pages-path';

const {iid} = useRouterSectionParams();

const api = useApi();
const router = useRouter();
const logger = useLogger();
const userStore = useUserStore();
const intervention = useCreateIntervention(null, 'intervention-fetch-page');

const [submitHandler, load, err] = useLoadingData(intervention, async() => api.me.getIntervention(iid.value!));

// Check if user needs medical device consent
const needsMedicalDeviceConsent = computed(() => {
  const consent = userStore.userData?.consents?.medicalDeviceConsent;
  return !consent || !consent.approved || consent.needsReapproval;
});

// Check consent on mount and redirect if needed
onMounted(() => {
  if (needsMedicalDeviceConsent.value) {
    void router.replace(pagesPath.intervention.interventionsList);
  }
});

watch(iid, async(value, oldValue) => {
  // Only fetch if consent is valid
  if (needsMedicalDeviceConsent.value) {
    return;
  }
  logger.debug(`Fetching intervention onWatch ${oldValue} -> ${value}`)();
  await submitHandler();
}, {immediate: true});

</script>
<template>
  <loading-error id="intervention-provider-page" :loading="load" :error="err" type="modal">
    <!-- Children assume a non-null intervention; the fetch is skipped when medical
         device consent is pending, so gate mounting until data is actually loaded -->
    <router-view v-if="intervention"/>
  </loading-error>
</template>
